/**
 * Update Adapter
 * Abstracts app update checking across platforms
 */

import type { Platform } from './platform';
import { ElectronUpdateAdapter } from './electron-update.adapter';
import { CapacitorUpdateAdapter } from './capacitor-update.adapter';

export interface UpdateInfo {
  version: string;
  releaseNotes?: string;
}

export type UpdateCallback = (info: UpdateInfo) => void;

export interface UpdateAdapter {
  checkForUpdate(): Promise<UpdateInfo | null>;
  downloadUpdate(): Promise<boolean>;
  installUpdate(): Promise<void>;
  onUpdateAvailable(callback: UpdateCallback): void;
}

class PwaUpdateAdapter implements UpdateAdapter {
  // PWA updates are handled by the service worker automatically
  async checkForUpdate(): Promise<UpdateInfo | null> {
    return null;
  }

  async downloadUpdate(): Promise<boolean> {
    return false;
  }

  async installUpdate(): Promise<void> {
    // Service worker handles this via registration.update()
  }

  onUpdateAvailable(): void {
    // No-op for PWA — service worker autoUpdate handles this
  }
}

let updateAdapterInstance: UpdateAdapter | null = null;

export function createUpdateAdapter(platform: Platform): UpdateAdapter {
  if (updateAdapterInstance) return updateAdapterInstance;

  switch (platform) {
    case 'electron':
      updateAdapterInstance = new ElectronUpdateAdapter();
      break;
    case 'capacitor':
      updateAdapterInstance = new CapacitorUpdateAdapter();
      break;
    case 'pwa':
    default:
      updateAdapterInstance = new PwaUpdateAdapter();
      break;
  }

  return updateAdapterInstance;
}

export function getUpdateAdapter(): UpdateAdapter {
  if (!updateAdapterInstance) {
    throw new Error('Update adapter not initialized. Call createUpdateAdapter() first.');
  }
  return updateAdapterInstance;
}
